import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { useAuth } from '@/context/AuthContext';
import api from '@/lib/api';
import Navbar from '@/components/Navbar';
import CoverImage from '@/components/CoverImage';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import {
  Image as ImageIcon,
  AlertTriangle,
  CheckCircle2,
  ExternalLink,
  Loader2,
  RefreshCw,
  Search,
  UploadCloud,
  ArrowLeft,
} from 'lucide-react';

const STATUS_CONFIG = {
  healthy:  { label: 'Saudável',    tone: 'bg-emerald-500/20 text-emerald-300 border-emerald-500/30', Icon: CheckCircle2 },
  missing:  { label: 'Em falta',     tone: 'bg-red-500/20 text-red-300 border-red-500/30',            Icon: AlertTriangle },
  empty:    { label: 'Sem imagem',   tone: 'bg-zinc-500/20 text-zinc-300 border-zinc-500/30',         Icon: ImageIcon },
  external: { label: 'Externa',      tone: 'bg-blue-500/20 text-blue-300 border-blue-500/30',        Icon: ExternalLink },
  unknown:  { label: 'Desconhecida', tone: 'bg-amber-500/20 text-amber-300 border-amber-500/30',     Icon: AlertTriangle },
};

function StatusBadge({ status }) {
  const cfg = STATUS_CONFIG[status] || STATUS_CONFIG.unknown;
  const Icon = cfg.Icon;
  return (
    <Badge className={`${cfg.tone} border px-2 py-0.5 gap-1`} data-testid={`media-status-${status}`}>
      <Icon className="w-3 h-3" />
      <span className="text-xs">{cfg.label}</span>
    </Badge>
  );
}

function StatCard({ label, value, tone = 'ruby', onClick, active, testId }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testId}
      className={`text-left p-4 rounded-xl border transition-all ${
        active
          ? 'bg-ruby/10 border-ruby'
          : 'bg-surface border-border hover:border-ruby/50'
      }`}
    >
      <div className="text-2xl font-bold text-foreground">{value}</div>
      <div className={`text-xs mt-1 ${active ? 'text-ruby' : 'text-muted-foreground'}`}>{label}</div>
    </button>
  );
}

function MediaThumb({ item }) {
  return (
    <div className="relative w-20 h-20 rounded-md overflow-hidden bg-surface shrink-0">
      <CoverImage
        src={item.status === 'missing' ? null : item.url}
        alt={item.content_title}
        Icon={item.status === 'missing' ? AlertTriangle : ImageIcon}
      />
    </div>
  );
}

function ReplaceDialog({ item, onClose, onReplaced }) {
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [uploading, setUploading] = useState(false);
  const inputRef = useRef();

  const pickFile = (f) => {
    if (!f) return;
    if (!f.type.startsWith('image/')) {
      toast.error('Ficheiro tem de ser uma imagem');
      return;
    }
    if (f.size > 5 * 1024 * 1024) {
      toast.error('Imagem demasiado grande (máx 5MB)');
      return;
    }
    setFile(f);
    const reader = new FileReader();
    reader.onload = (e) => setPreview(e.target.result);
    reader.readAsDataURL(f);
  };

  const submit = async () => {
    if (!file) return;
    setUploading(true);
    const fd = new FormData();
    fd.append('file', file);
    try {
      const res = await api.post(
        `/admin/media/${item.content_type}/${item.content_id}/replace`,
        fd,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );
      toast.success('Imagem substituída com sucesso');
      onReplaced(res.data);
    } catch (err) {
      const msg = err?.response?.data?.detail || 'Falha ao substituir imagem';
      toast.error(msg);
    } finally {
      setUploading(false);
    }
  };

  return (
    <Dialog open={!!item} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl bg-surface border-border" data-testid="media-replace-dialog">
        <DialogHeader>
          <DialogTitle className="text-foreground">Substituir imagem</DialogTitle>
          <DialogDescription>
            {item?.content_title}
            <span className="block text-xs mt-1 opacity-70">
              {item?.content_type_label} · {item?.content_id?.slice(0, 8)}
            </span>
          </DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <div className="text-xs text-muted-foreground mb-2">Imagem atual</div>
            <div className="relative aspect-video rounded-lg overflow-hidden bg-background border border-border">
              <CoverImage
                src={item?.status === 'missing' ? null : item?.url}
                alt=""
                Icon={item?.status === 'missing' ? AlertTriangle : ImageIcon}
              />
            </div>
            <div className="text-xs text-muted-foreground mt-2 break-all">
              {item?.filename || item?.legacy_path || '—'}
            </div>
          </div>

          <div>
            <div className="text-xs text-muted-foreground mb-2">Nova imagem</div>
            <div
              className="relative aspect-video rounded-lg overflow-hidden bg-background border border-dashed border-border hover:border-ruby cursor-pointer flex items-center justify-center"
              onClick={() => inputRef.current?.click()}
              data-testid="media-replace-dropzone"
            >
              {preview ? (
                <img src={preview} alt="preview" className="w-full h-full object-cover" />
              ) : (
                <div className="text-center px-4">
                  <UploadCloud className="w-8 h-8 mx-auto text-ruby mb-2" />
                  <div className="text-sm text-foreground">Clica para escolher</div>
                  <div className="text-xs text-muted-foreground mt-1">JPG · PNG · WEBP · até 5MB</div>
                </div>
              )}
            </div>
            <input
              ref={inputRef}
              type="file"
              accept="image/*"
              className="hidden"
              onChange={(e) => pickFile(e.target.files?.[0])}
              data-testid="media-replace-file-input"
            />
            {file && (
              <div className="text-xs text-muted-foreground mt-2 break-all">
                {file.name} · {(file.size / 1024).toFixed(0)} KB
              </div>
            )}
          </div>
        </div>

        <div className="text-xs text-muted-foreground bg-background/50 rounded p-3 border border-border">
          A imagem será enviada para <span className="text-ruby font-mono">Bunny Storage</span>,
          verificada na origem e só então associada ao conteúdo. A referência legacy será
          preservada para auditoria.
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={onClose} disabled={uploading} data-testid="media-replace-cancel">
            Cancelar
          </Button>
          <Button
            onClick={submit}
            disabled={!file || uploading}
            className="bg-ruby hover:bg-ruby-light text-white"
            data-testid="media-replace-submit"
          >
            {uploading ? (
              <><Loader2 className="w-4 h-4 mr-2 animate-spin" />A substituir…</>
            ) : (
              <><UploadCloud className="w-4 h-4 mr-2" />Confirmar substituição</>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function AdminMedia() {
  const navigate = useNavigate();
  const { user } = useAuth();

  const [stats, setStats] = useState(null);
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [pages, setPages] = useState(1);
  const [page, setPage] = useState(1);

  const [status, setStatus] = useState('missing');   // default view: recovery queue
  const [contentType, setContentType] = useState('all');
  const [q, setQ] = useState('');
  const [loading, setLoading] = useState(true);

  const [replaceTarget, setReplaceTarget] = useState(null);

  useEffect(() => {
    if (!user?.is_admin) navigate('/');
  }, [user, navigate]);

  const loadStats = useCallback(async () => {
    try {
      const res = await api.get('/admin/media/stats');
      setStats(res.data);
    } catch (err) {
      toast.error('Falha ao carregar estatísticas');
    }
  }, []);

  const loadItems = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, limit: 50 };
      if (status !== 'all') params.status = status;
      if (contentType !== 'all') params.content_type = contentType;
      if (q.trim()) params.q = q.trim();
      const res = await api.get('/admin/media', { params });
      setItems(res.data.items);
      setTotal(res.data.total);
      setPages(res.data.pages);
    } catch (err) {
      toast.error('Falha ao carregar biblioteca');
    } finally {
      setLoading(false);
    }
  }, [page, status, contentType, q]);

  useEffect(() => { loadStats(); }, [loadStats]);
  useEffect(() => { loadItems(); }, [loadItems]);

  useEffect(() => { setPage(1); }, [status, contentType, q]);

  const onReplaced = (result) => {
    setReplaceTarget(null);
    // Refresh both stats + list to reflect new status
    loadStats();
    loadItems();
  };

  const totals = stats?.totals;
  const missing = totals?.missing ?? 0;

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      <main className="max-w-7xl mx-auto px-4 pt-24 pb-16">

        {/* Header */}
        <div className="flex items-center gap-3 mb-6">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate('/admin')}
            data-testid="media-back-btn"
          >
            <ArrowLeft className="w-4 h-4 mr-1" /> Painel
          </Button>
          <div className="flex-1">
            <h1 className="text-2xl md:text-3xl font-bold text-foreground">
              Biblioteca de Media
            </h1>
            <p className="text-sm text-muted-foreground mt-1">
              Todas as imagens do Intimate, alojadas na Bunny Storage.
            </p>
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => { loadStats(); loadItems(); }}
            data-testid="media-refresh-btn"
          >
            <RefreshCw className="w-4 h-4 mr-1" /> Recarregar
          </Button>
        </div>

        {/* Missing banner */}
        {missing > 0 && (
          <div
            className="mb-6 flex items-start gap-3 rounded-xl border border-red-500/40 bg-red-500/10 p-4"
            data-testid="media-missing-banner"
          >
            <AlertTriangle className="w-5 h-5 text-red-400 shrink-0 mt-0.5" />
            <div className="flex-1">
              <div className="text-foreground font-semibold">
                {missing} {missing === 1 ? 'imagem em falta' : 'imagens em falta'}
              </div>
              <div className="text-sm text-muted-foreground mt-1">
                Estas capas foram perdidas antes da migração para Bunny.net. Substitui-as
                uma a uma para restaurar o catálogo.
              </div>
            </div>
            <Button
              size="sm"
              variant="outline"
              onClick={() => { setStatus('missing'); setContentType('all'); }}
              data-testid="media-jump-missing"
            >
              Ver todas
            </Button>
          </div>
        )}

        {/* Stat cards / filters */}
        <div className="grid grid-cols-2 md:grid-cols-6 gap-3 mb-6">
          <StatCard label="Total" value={totals?.records ?? '—'} onClick={() => setStatus('all')} active={status === 'all'} testId="media-stat-all" />
          <StatCard label="Em falta" value={totals?.missing ?? '—'} tone="red" onClick={() => setStatus('missing')} active={status === 'missing'} testId="media-stat-missing" />
          <StatCard label="Saudáveis" value={totals?.healthy ?? '—'} onClick={() => setStatus('healthy')} active={status === 'healthy'} testId="media-stat-healthy" />
          <StatCard label="Externas" value={totals?.external ?? '—'} onClick={() => setStatus('external')} active={status === 'external'} testId="media-stat-external" />
          <StatCard label="Sem imagem" value={totals?.empty ?? '—'} onClick={() => setStatus('empty')} active={status === 'empty'} testId="media-stat-empty" />
          <StatCard label="Bunny · não usadas" value={totals?.unused_objects ?? '—'} testId="media-stat-unused" />
        </div>

        {/* Filters */}
        <div className="flex flex-col md:flex-row gap-3 mb-6">
          <div className="relative flex-1">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input
              placeholder="Procurar por título ou ficheiro…"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              className="pl-9 bg-surface border-border"
              data-testid="media-search-input"
            />
          </div>
          <Select value={contentType} onValueChange={setContentType}>
            <SelectTrigger className="w-full md:w-64 bg-surface border-border" data-testid="media-type-filter">
              <SelectValue placeholder="Tipo de conteúdo" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos os tipos</SelectItem>
              {Object.entries(stats?.by_content_type || {}).map(([key, cfg]) => (
                <SelectItem key={key} value={key}>
                  {cfg.label} ({cfg.total})
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Items list */}
        {loading ? (
          <div className="flex justify-center py-16">
            <Loader2 className="w-6 h-6 animate-spin text-ruby" />
          </div>
        ) : items.length === 0 ? (
          <Card className="bg-surface border-border">
            <CardContent className="py-12 text-center text-muted-foreground">
              {status === 'missing' ? (
                <>
                  <CheckCircle2 className="w-10 h-10 mx-auto text-emerald-400 mb-3" />
                  <div className="text-foreground font-medium">Nenhuma imagem em falta.</div>
                  <div className="text-sm mt-1">Todas as capas estão a servir da CDN Bunny.</div>
                </>
              ) : (
                <>Nenhum resultado para os filtros seleccionados.</>
              )}
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-2" data-testid="media-list">
            {items.map((it) => (
              <MediaRow key={it.media_id} item={it} onReplace={() => setReplaceTarget(it)} />
            ))}
          </div>
        )}

        {/* Pagination */}
        {pages > 1 && (
          <div className="flex items-center justify-center gap-3 mt-6">
            <Button
              variant="outline"
              size="sm"
              disabled={page <= 1}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
            >
              Anterior
            </Button>
            <span className="text-sm text-muted-foreground">
              Página {page} de {pages} · {total} entradas
            </span>
            <Button
              variant="outline"
              size="sm"
              disabled={page >= pages}
              onClick={() => setPage((p) => Math.min(pages, p + 1))}
            >
              Seguinte
            </Button>
          </div>
        )}
      </main>

      {replaceTarget && (
        <ReplaceDialog
          item={replaceTarget}
          onClose={() => setReplaceTarget(null)}
          onReplaced={onReplaced}
        />
      )}
    </div>
  );
}

function MediaRow({ item, onReplace }) {
  return (
    <div
      className="flex items-center gap-4 bg-surface border border-border rounded-xl p-3 hover:border-ruby/40 transition-colors"
      data-testid={`media-row-${item.content_id}`}
    >
      <MediaThumb item={item} />
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap mb-1">
          <StatusBadge status={item.status} />
          <span className="text-xs text-muted-foreground">{item.content_type_label}</span>
          {item.upload_date && (
            <span className="text-xs text-muted-foreground">· {item.upload_date}</span>
          )}
        </div>
        <div className="text-foreground font-medium truncate" title={item.content_title}>
          {item.content_title}
        </div>
        <div className="text-xs text-muted-foreground truncate font-mono" title={item.filename}>
          {item.filename || item.legacy_path || '—'}
        </div>
        {item.status_reason && (
          <div className="text-xs text-red-300 mt-1">{item.status_reason}</div>
        )}
      </div>
      <div className="flex items-center gap-2 shrink-0">
        {item.admin_path && (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => window.open(item.admin_path, '_blank')}
            data-testid={`media-open-content-${item.content_id}`}
          >
            <ExternalLink className="w-4 h-4" />
          </Button>
        )}
        <Button
          size="sm"
          onClick={onReplace}
          className="bg-ruby hover:bg-ruby-light text-white"
          data-testid={`media-replace-btn-${item.content_id}`}
        >
          <UploadCloud className="w-4 h-4 mr-1" />
          Substituir imagem
        </Button>
      </div>
    </div>
  );
}
